import { useEffect, useState } from "react";
import api from "@/lib/api";
import { compact, inr, CHANNEL_COLORS } from "@/lib/format";
import { DaysToggle, Skeleton } from "@/components/Shared";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell } from "recharts";

export default function Channels() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/dashboard/channels?days=${days}`).then(({ data }) => setData(data));
  }, [days]);

  if (!data) return <div className="space-y-6"><Skeleton className="h-64" /><Skeleton className="h-72" /></div>;

  return (
    <div className="space-y-6" data-testid="channels-page">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Live channel performance — last {days} days.</p>
        <DaysToggle value={days} onChange={setDays} />
      </div>
      {data.pending?.length > 0 && (
        <div className="bg-muted/60 border border-border rounded-lg px-4 py-3 text-sm text-muted-foreground" data-testid="pending-channels-banner">
          {data.pending.map((p) => <span key={p.channel} className="mr-6"><strong className="text-foreground">{p.channel}:</strong> {p.reason}</span>)}
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-card border border-border rounded-lg p-6 shadow-sm">
          <h3 className="font-heading text-base font-semibold tracking-tight mb-5">Monthly revenue by channel</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.monthly}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => compact(v)} stroke="hsl(var(--muted-foreground))" width={70} />
              <Tooltip formatter={(v, n) => [inr(v), n]} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
              {data.channels.map((c) => (
                <Bar key={c.channel} dataKey={c.channel} fill={CHANNEL_COLORS[c.channel]} radius={[3, 3, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
          <h3 className="font-heading text-base font-semibold tracking-tight mb-5">Revenue share</h3>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={data.channels} dataKey="revenue" nameKey="channel" innerRadius={55} outerRadius={85} paddingAngle={3}>
                {data.channels.map((c) => <Cell key={c.channel} fill={CHANNEL_COLORS[c.channel]} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [inr(v), n]} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-1.5 mt-2">
            {data.channels.map((c) => (
              <div key={c.channel} className="flex items-center justify-between text-xs">
                <span className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-sm" style={{ background: CHANNEL_COLORS[c.channel] }} />{c.channel}</span>
                <span className="font-semibold">{c.share}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden" data-testid="channels-table">
        <table className="w-full text-sm">
          <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground sticky top-0">
            <tr>
              {["Channel", "Revenue", "Orders", "Units", "AOV", "Fees", "Net Revenue", "Refunds", "Growth"].map((h) => (
                <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.channels.map((c) => (
              <tr key={c.channel} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`channel-row-${c.channel.toLowerCase()}`}>
                <td className="px-4 py-3 font-semibold flex items-center gap-2">
                  <span className="h-2.5 w-2.5 rounded-sm" style={{ background: CHANNEL_COLORS[c.channel] }} />{c.channel}
                </td>
                <td className="px-4 py-3">{inr(c.revenue)}</td>
                <td className="px-4 py-3">{c.orders.toLocaleString("en-IN")}</td>
                <td className="px-4 py-3">{c.units.toLocaleString("en-IN")}</td>
                <td className="px-4 py-3">{inr(c.aov)}</td>
                <td className="px-4 py-3 text-red-600">{inr(c.fees)}</td>
                <td className="px-4 py-3 font-semibold text-green-600">{inr(c.net)}</td>
                <td className="px-4 py-3">{inr(c.refunds)}</td>
                <td className={`px-4 py-3 font-semibold ${c.growth >= 0 ? "text-green-600" : "text-red-600"}`}>{c.growth >= 0 ? "+" : ""}{c.growth}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
